import React, { useEffect, useState } from 'react';
import { Card, Button, Badge, EmptyState, ErrorNote } from '../ui';

interface JobRow {
  id: string;
  jobType: string;
  status: string;
  attemptCount: number;
  maxAttempts: number;
  runAfter: string;
  lastErrorCode: string | null;
  lastErrorMessage: string | null;
  blockedReason: string | null;
  updatedAt: string;
  contentItemId: string | null;
  contentItem: { id: string; title: string | null; contentType: string; state: string; page: { internalLabel: string; timeZone: string } } | null;
}

const FILTERS = ['ALL', 'PENDING', 'RUNNING', 'RETRY_WAIT', 'BLOCKED', 'FAILED', 'SUCCEEDED'] as const;

// The pipeline stages a post moves through, in order, with friendly labels.
const STAGES: { key: string; label: string; match: (t: string) => boolean }[] = [
  { key: 'plan', label: 'Plan', match: (t) => t.startsWith('plan.') },
  { key: 'title', label: 'Title', match: (t) => t.includes('title') },
  { key: 'script', label: 'Script', match: (t) => t.includes('script') },
  { key: 'reference', label: 'Reference', match: (t) => t.includes('reference') },
  { key: 'media', label: 'Media', match: (t) => t.includes('image') || t.includes('video') || t.includes('segment') || t.includes('thumbnail') || t.includes('reel') },
  { key: 'caption', label: 'Caption', match: (t) => t.includes('caption') },
  { key: 'review', label: 'Review', match: (t) => t.includes('review') },
  { key: 'schedule', label: 'Schedule', match: (t) => t.includes('schedule') },
  { key: 'publish', label: 'Publish', match: (t) => t.includes('publish') },
];

function stageOf(jobType: string): string {
  return STAGES.find((s) => s.match(jobType))?.key ?? 'media';
}

const STATUS_BADGE: Record<string, string> = {
  SUCCEEDED: 'HEALTHY', RUNNING: 'BUSY', PENDING: 'BUSY', CLAIMED: 'BUSY',
  RETRY_WAIT: 'RATE_LIMITED', BLOCKED: 'MISCONFIGURED', FAILED: 'MISCONFIGURED', CANCELLED: 'ARCHIVED',
};

/** Pretty stage-dot state colors for the pipeline row. */
function dotClass(state: 'done' | 'active' | 'wait' | 'fail' | 'idle'): string {
  return {
    done: 'bg-emerald-500 text-emerald-950',
    active: 'bg-brand-500 text-white animate-pulse',
    wait: 'bg-amber-500/80 text-amber-950',
    fail: 'bg-red-500 text-white',
    idle: 'bg-neutral-800 text-neutral-600',
  }[state];
}

export function QueueScreen({ pageId }: { pageId?: string } = {}) {
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('ALL');
  const [error, setError] = useState<string | null>(null);
  const [showSystem, setShowSystem] = useState(false);

  const load = async () => {
    try {
      setJobs((await window.pagepilot.jobs.list(filter, pageId)) as JobRow[]);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 6_000);
    return () => clearInterval(t);
  }, [filter, pageId]);

  // Split content posts (grouped by content item) from infrastructure/system jobs.
  const contentJobs = jobs.filter((j) => j.contentItem);
  const systemJobs = jobs.filter((j) => !j.contentItem);

  const postQueues = Array.from(
    contentJobs.reduce((groups, job) => {
      const key = job.contentItem!.id;
      const group = groups.get(key) ?? { item: job.contentItem, jobs: [] as JobRow[] };
      group.jobs.push(job);
      groups.set(key, group);
      return groups;
    }, new Map<string, { item: JobRow['contentItem']; jobs: JobRow[] }>()).values(),
  );

  const counts = {
    running: jobs.filter((j) => j.status === 'RUNNING' || j.status === 'CLAIMED').length,
    pending: jobs.filter((j) => j.status === 'PENDING' || j.status === 'RETRY_WAIT').length,
    problem: jobs.filter((j) => j.status === 'FAILED' || j.status === 'BLOCKED').length,
    done: jobs.filter((j) => j.status === 'SUCCEEDED').length,
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-semibold">Publishing Queue</h1>
        <div className="flex items-center gap-1.5 text-xs">
          <span className="rounded-full bg-brand-500/15 px-2 py-0.5 text-brand-300">{counts.running} running</span>
          <span className="rounded-full bg-neutral-800 px-2 py-0.5 text-neutral-300">{counts.pending} waiting</span>
          {counts.problem > 0 && <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-red-300">{counts.problem} problem</span>}
          <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-emerald-300">{counts.done} done</span>
        </div>
        <div className="ml-auto flex gap-1">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`rounded-full px-3 py-1 text-xs ${filter === f ? 'bg-brand-500/20 text-brand-300' : 'text-neutral-500 hover:text-neutral-300'}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>
      {error && <ErrorNote message={error} />}

      {postQueues.length === 0 && systemJobs.length === 0 ? (
        <Card><EmptyState title="Queue is empty" hint="Each post runs through its own pipeline here. When you generate content, its stages (title → media → caption → review → schedule → publish) appear as a card you can track and retry." /></Card>
      ) : (
        postQueues.map((queue) => {
          const item = queue.item!;
          // Determine per-stage state from this post's jobs.
          const byStage = new Map<string, JobRow[]>();
          for (const j of queue.jobs) {
            const k = stageOf(j.jobType);
            byStage.set(k, [...(byStage.get(k) ?? []), j]);
          }
          return (
            <Card key={item.id}>
              <div className="mb-3 flex items-start justify-between gap-3 border-b border-neutral-800 pb-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-neutral-200">{item.title ?? 'Generating…'}</p>
                  <p className="text-xs text-neutral-500">
                    {!pageId && `${item.page.internalLabel} · `}{item.contentType === 'REEL' ? 'Reel' : 'Image post'} · {item.state.replaceAll('_', ' ').toLowerCase()}
                  </p>
                </div>
                <span className="rounded-full bg-neutral-800 px-2 py-1 text-[10px] text-neutral-400">{queue.jobs.length} step{queue.jobs.length === 1 ? '' : 's'}</span>
              </div>

              {/* Pipeline overview */}
              <div className="mb-3 flex flex-wrap items-center gap-1">
                {STAGES.filter((s) => s.key !== 'plan').map((s, idx) => {
                  const stageJobs = byStage.get(s.key) ?? [];
                  const has = stageJobs.length > 0;
                  const anyFail = stageJobs.some((j) => j.status === 'FAILED' || j.status === 'BLOCKED');
                  const anyActive = stageJobs.some((j) => j.status === 'RUNNING' || j.status === 'CLAIMED');
                  const anyWait = stageJobs.some((j) => j.status === 'PENDING' || j.status === 'RETRY_WAIT');
                  const allDone = has && stageJobs.every((j) => j.status === 'SUCCEEDED');
                  const state = !has ? 'idle' : anyFail ? 'fail' : anyActive ? 'active' : anyWait ? 'wait' : allDone ? 'done' : 'idle';
                  return (
                    <React.Fragment key={s.key}>
                      {idx > 0 && <span className="text-neutral-700">›</span>}
                      <span
                        title={`${s.label}: ${has ? stageJobs.map((j) => j.status).join(', ') : 'not started'}`}
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] ${dotClass(state as 'done')}`}
                      >
                        {s.label}
                      </span>
                    </React.Fragment>
                  );
                })}
              </div>

              <table className="w-full text-left text-xs">
                <thead className="text-neutral-500"><tr><th className="pb-2 pr-3 font-medium">Step</th><th className="pb-2 pr-3 font-medium">Status</th><th className="pb-2 pr-3 font-medium">Attempts</th><th className="pb-2 pr-3 font-medium">Next run</th><th className="pb-2 pr-3 font-medium">Problem</th><th className="pb-2 font-medium">Actions</th></tr></thead>
                <tbody>
                  {queue.jobs.map((j) => (
                    <tr key={j.id} className="border-t border-neutral-800/60 align-top">
                      <td className="py-2 pr-3 text-neutral-300">{STAGES.find((s) => s.match(j.jobType))?.label ?? j.jobType}</td>
                      <td className="py-2 pr-3"><Badge status={STATUS_BADGE[j.status] ?? 'BUSY'} label={j.status} /></td>
                      <td className="py-2 pr-3 text-neutral-400">{j.attemptCount}/{j.maxAttempts}</td>
                      <td className="py-2 pr-3 text-neutral-500">{new Date(j.runAfter).toLocaleString()}</td>
                      <td className="max-w-[320px] py-2 pr-3 text-red-400/90">{j.blockedReason ?? j.lastErrorMessage ?? ''}</td>
                      <td className="py-2 whitespace-nowrap">
                        {['RETRY_WAIT', 'BLOCKED', 'FAILED'].includes(j.status) && <button className="mr-2 text-brand-300 hover:underline" onClick={async () => { await window.pagepilot.engine.jobRetry(j.id); void load(); }}>Retry</button>}
                        {['PENDING', 'RETRY_WAIT', 'BLOCKED', 'RUNNING'].includes(j.status) && <button className="text-neutral-500 hover:text-red-400 hover:underline" onClick={async () => { await window.pagepilot.engine.jobCancel(j.id); void load(); }}>Cancel</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          );
        })
      )}

      {/* System / infrastructure tasks (token refresh, daily planning) — not tied to one post. */}
      {systemJobs.length > 0 && (
        <Card>
          <button className="flex w-full items-center justify-between text-left" onClick={() => setShowSystem((v) => !v)}>
            <span className="text-sm font-semibold text-neutral-300">System tasks ({systemJobs.length})</span>
            <span className="text-xs text-neutral-500">{showSystem ? 'Hide' : 'Show'}</span>
          </button>
          {showSystem && (
            <table className="mt-3 w-full text-left text-xs">
              <thead className="text-neutral-500"><tr><th className="pb-2 pr-3 font-medium">Task</th><th className="pb-2 pr-3 font-medium">Status</th><th className="pb-2 pr-3 font-medium">Attempts</th><th className="pb-2 pr-3 font-medium">Problem</th><th className="pb-2 font-medium">Actions</th></tr></thead>
              <tbody>
                {systemJobs.map((j) => (
                  <tr key={j.id} className="border-t border-neutral-800/60 align-top">
                    <td className="py-2 pr-3 font-mono text-neutral-300">{j.jobType}</td>
                    <td className="py-2 pr-3"><Badge status={STATUS_BADGE[j.status] ?? 'BUSY'} label={j.status} /></td>
                    <td className="py-2 pr-3 text-neutral-400">{j.attemptCount}/{j.maxAttempts}</td>
                    <td className="max-w-[320px] py-2 pr-3 text-red-400/90">{j.blockedReason ?? j.lastErrorMessage ?? ''}</td>
                    <td className="py-2 whitespace-nowrap">
                      {['RETRY_WAIT', 'BLOCKED', 'FAILED'].includes(j.status) && <button className="mr-2 text-brand-300 hover:underline" onClick={async () => { await window.pagepilot.engine.jobRetry(j.id); void load(); }}>Retry</button>}
                      {['PENDING', 'RETRY_WAIT', 'BLOCKED', 'RUNNING'].includes(j.status) && <button className="text-neutral-500 hover:text-red-400 hover:underline" onClick={async () => { await window.pagepilot.engine.jobCancel(j.id); void load(); }}>Cancel</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      <div><Button variant="ghost" onClick={() => void load()}>Refresh</Button></div>
    </div>
  );
}
